import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../../../components/Breadcrumbs';
import Button from '../../../components/button';
import FileUpload from '../../../components/FileUpload';
import Input from '../../../components/input';
import PhoneNumberInput from '../../../components/PhoneNumberInput';
import Select from '../../../components/Select';
import GoogleMapInput from '../../../components/GoogleMapInput';
import { activeDetailsIcon, detailsIcon } from '../../../utils/svgs';
import '../../../styles/Trip.css';
import '../../../styles/VendorDetails.css';
import PermissionWrapper from '../../../components/PermissionWrapper';
import { AppMainModules, InventorySubModules, VendorAndWarehousePermission } from '../../../utils/app-constant';
import { updateVendor, getVendorDetail } from '../../../store/Slices/PartVendorSlice';
import { successNotify, errorNotify } from '../../../utils/toast';
import { fetchPartCategoriesDropdown } from '../../../store/Slices/dropdownSlice';
import { Formik } from 'formik';
import { VendorSchema } from '../../../utils/Schema';
import { normalizePhoneNumberValue } from '../../../pages/ThreePL/shared/phoneValidation';

const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
];

const VendorDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    const heading = 'Vendor Details';
    const requiredStar = <span className="required_star">*</span>;

    const initialRecord = useMemo(() => {
        const data = location.state?.data || {};
        return {
            id: data.id,
            name: data.name || data.vendorName || '',
            address: {
                vendor_point: data.location || data.address || '',
                vendor_latitude: data.lat || '',
                vendor_longitude: data.lng || ''
            },
            pocName: data.poc_name || '',
            pocContact: normalizePhoneNumberValue(data.poc_contact || ''),
            pocEmail: data.poc_email || '',
            category: data.categories ? data.categories.map(c => ({ label: c.title, value: c.id })) : [],
            status: data.status ? { label: data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase(), value: data.status } : null,
            notes: data.notes || '',
            attachments: []
        };
    }, [location.state]);

    const [formInitialValues, setFormInitialValues] = useState(initialRecord);
    const [attachments, setAttachments] = useState([]);

    const { updateVendorLoading, vendorDetail } = useSelector((state) => state.partVendors);
    const { loading: dropdownLoading, partCategoriesDropdownData } = useSelector((state) => state.dropdowns);

    useEffect(() => {
        dispatch(fetchPartCategoriesDropdown());
        if (params.id) {
            dispatch(getVendorDetail(params.id));
        }
    }, [dispatch, params.id]);

    useEffect(() => {
        if (vendorDetail) {
            const data = vendorDetail;
            setFormInitialValues({
                id: data.id,
                name: data.name || data.vendorName || '',
                address: {
                    vendor_point: data.location || data.address || '',
                    vendor_latitude: data.lat || '',
                    vendor_longitude: data.lng || ''
                },
                pocName: data.primary_poc_name || data.poc_name || data.primaryPOC || '',
                pocContact: normalizePhoneNumberValue(data.poc_contact || data.contactNumber || ''),
                pocEmail: data.poc_email || data.email || '',
                category: data.categories ? data.categories.map(c => ({ label: c.title, value: c.id })) : [],
                status: data.status ? { label: data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase(), value: data.status } : null,
                notes: data.notes || '',
                attachments: []
            });
            setAttachments((data.files || []).map(f => {
                if (typeof f === 'string') {
                    return { fileName: f, originalName: f };
                }
                const name = f.fileName || f.file_name || f.file_path || f.url || f.path || f.file || f.image || '';
                return {
                    ...f,
                    fileName: name,
                    originalName: f.originalName || f.original_name || name
                };
            }));
        }
    }, [vendorDetail, initialRecord.notes]);

    return (
        <Formik
            enableReinitialize={true}
            initialValues={formInitialValues}
            validationSchema={VendorSchema}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    const payload = {
                        part_vendor_id: initialRecord.id || params.id,
                        name: values.name,
                        location: values.address?.vendor_point,
                        lat: values.address?.vendor_latitude,
                        lng: values.address?.vendor_longitude,
                        poc_name: values.pocName,
                        poc_contact: values.pocContact,
                        poc_email: values.pocEmail,
                        category_ids: values.category ? (Array.isArray(values.category) ? values.category.map(c => c.value) : [values.category.value]) : [],
                        status: values.status?.value?.toLowerCase(),
                        files: attachments.length > 0 ? attachments.map(f => f.fileName || f) : [],
                        notes: values.notes
                    };

                    await dispatch(updateVendor(payload)).unwrap();
                    successNotify("Vendor updated successfully");
                    navigate('/vendor');
                } catch (error) {
                    errorNotify(error);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                isSubmitting
            }) => (
                <div className="vendor_details_page">
                    <Breadcrumbs values={['Vendor & Warehouse', 'Vendor', heading]} />

                    <div className="page_header">
                        <h2>{heading}</h2>
                        <div className="d-flex align-items-center gap-2">
                            <PermissionWrapper
                                module={InventorySubModules.Vendor}
                                permission={VendorAndWarehousePermission.UpdateVendor}
                                mainModule={AppMainModules.inventoryManagement}
                            >
                                <Button
                                    cancelBtn={() => navigate('/vendor')}
                                    submitText="Save Details"
                                    type="button"
                                    shouldRow={false}
                                    isShowSubmitBtn={true}
                                    submitBtn={handleSubmit}
                                    loading={updateVendorLoading}
                                    disabled={updateVendorLoading}
                                />
                            </PermissionWrapper>
                        </div>
                    </div>

                    <div className="page_container">
                        <Row className="vendor_details_row">
                            <Col md={9}>
                                <div className="content_box">
                                    <h6>Vendor Information</h6>
                                    <p>Main information for this record.</p>

                                    <Row>
                                        <Col md={6}>
                                            <Input
                                                label={<>Vendor Name {requiredStar}</>}
                                                placeholder="Enter Vendor Name"
                                                name="name"
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.name}
                                                touched={touched.name}
                                                required={false}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <GoogleMapInput
                                                label={<>Address {requiredStar}</>}
                                                name="address"
                                                value={values.address}
                                                setFieldValue={setFieldValue}
                                                error={errors.address}
                                                touched={touched.address}
                                                fieldPrefix="vendor"
                                                required={false}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Input
                                                label={<>POC Name {requiredStar}</>}
                                                placeholder="Enter POC Name"
                                                name="pocName"
                                                value={values.pocName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.pocName}
                                                touched={touched.pocName}
                                                required={false}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <PhoneNumberInput
                                                label={<>POC Contact {requiredStar}</>}
                                                placeholder="923XXXXXXXXX"
                                                name="pocContact"
                                                value={values.pocContact}
                                                setFieldValue={setFieldValue}
                                                onBlur={handleBlur}
                                                error={errors.pocContact}
                                                touched={touched.pocContact}
                                                required={false}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Select
                                                label={<>Category {requiredStar}</>}
                                                placeholder="Select Category"
                                                options={partCategoriesDropdownData}
                                                value={values.category}
                                                loading={dropdownLoading}
                                                onChange={(val) => setFieldValue('category', val)}
                                                onBlur={handleBlur}
                                                error={errors.category}
                                                touched={touched.category}
                                                isMulti={true}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Select
                                                label={<>Status {requiredStar}</>}
                                                placeholder="Select Status"
                                                options={statusOptions}
                                                value={values.status}
                                                onChange={(val) => setFieldValue('status', val)}
                                                onBlur={handleBlur}
                                                error={errors.status}
                                                touched={touched.status}
                                            />
                                        </Col>
                                    </Row>
                                </div>

                                <div className="content_box">
                                    <h6>Attachments</h6>
                                    <p>Upload relevant photos or documents for this record.</p>
                                    <div className="issue_file_upload_wrapper">
                                        <label>Attachments</label>
                                        <FileUpload
                                            destination="vendor-attachments"
                                            files={attachments}
                                            setFiles={setAttachments}
                                            maxFileSize={10}
                                            className="issue_file_upload"
                                        />
                                    </div>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="service_cost_summary_details">
                                    <div className="parts_general_notes">
                                        <h5>General Notes</h5>
                                        <p>Add any additional notes or observations here.</p>
                                        <textarea
                                            rows="12"
                                            style={{
                                                width: '100%',
                                                border: '1px solid #E3E3E8',
                                                borderRadius: '8px',
                                                padding: '10px',
                                                background: '#F9F9FB',
                                                color: '#3D3F5C',
                                                fontSize: '14px'
                                            }}
                                            value={values.notes}
                                            onChange={handleChange}
                                            name="notes"
                                        />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>
            )}
        </Formik>
    );
};

export default VendorDetails;
